import { AttachmentFile } from '@nextforce/api'
import { Button, Icon, Text, makeStyles } from '@rneui/themed'
import { useRoutingContext } from 'app/routing_context'
import { useSendMessageContext } from 'chat/send_message_context'
import { Image, ImageStyle } from 'expo-image'
import React, { useMemo, useState } from 'react'
import {
  ActivityIndicator,
  ColorValue,
  Platform,
  StyleProp,
  View,
  ViewStyle,
  useWindowDimensions,
} from 'react-native'
import { FlatList } from 'react-native-gesture-handler'
import { WEB_SCREEN_WIDTH } from 'shared/constants'
import { generateThumbnail } from 'shared/video_thumbnail'
import { PressableOpacity } from '../pressable_opacity'

export const ATTACHMENT_PREVIEW_SIZE = 80
export const ATTACHMENT_PREVIEW_MARGIN_VERTICAL = 5
export const ATTACHMENT_PREVIEW_MARGIN_HORIZONTAL = 10
export function AttachmentPreviewRow({
  hideTitle = false,
  chatGroupId,
}: {
  hideTitle?: boolean
  chatGroupId: string
}) {
  const {
    getSelectedAttachments,
    clearAttachments,
    getFileUploadStatus,
    removeAttachment,
    editAttachment,
  } = useSendMessageContext()
  const dimensions = useWindowDimensions()
  const selectedAttachments = useMemo(
    () => getSelectedAttachments(chatGroupId),
    [chatGroupId, getSelectedAttachments]
  )
  const isUploading = getFileUploadStatus(chatGroupId)?.progress !== undefined

  const styles = useStyles()
  const webWidth =
    WEB_SCREEN_WIDTH < dimensions.width ? WEB_SCREEN_WIDTH : dimensions.width
  return selectedAttachments.length ? (
    <>
      <View
        style={[
          styles.attachmentPreviewRowContainer,
          !hideTitle && {
            height:
              styles.attachmentPreviewRowContainer.height +
              styles.listText.fontSize,
          },
          Platform.OS === 'web' && {
            width:
              webWidth -
              styles.attachmentPreviewRowContainer.marginHorizontal * 2,
          },
        ]}
      >
        {hideTitle ? null : (
          <Text
            style={styles.listText}
          >{`Attachments (${selectedAttachments.length})`}</Text>
        )}
        <FlatList
          keyboardShouldPersistTaps="handled"
          data={selectedAttachments}
          showsHorizontalScrollIndicator={true}
          horizontal
          keyExtractor={item => item.uri}
          renderItem={({ item }) => (
            <AttachmentPreview
              attachment={item}
              isUploading={isUploading}
              onPressRemove={() => removeAttachment(chatGroupId, item.uri)}
              onPressEdit={() => editAttachment(chatGroupId, item.uri)}
            />
          )}
          contentContainerStyle={styles.attachmentPreviewRowContentContainer}
          ListFooterComponent={() => (
            <View style={styles.footerContainer}>
              <Button
                disabled={isUploading}
                title={'Clear'}
                type="outline"
                size="md"
                onPress={() => clearAttachments(chatGroupId)}
                buttonStyle={styles.clearButton}
              />
            </View>
          )}
        />
      </View>
    </>
  ) : null
}

export function AttachmentPreview({
  attachment,

  isUploading,
  containerStyle,
  imageStyle,
  removeAttachmentIconColor,
  onPressEdit,
  onPressRemove,
}: {
  attachment: AttachmentFile
  isUploading?: boolean
  imageStyle?: StyleProp<ImageStyle>
  containerStyle?: StyleProp<ViewStyle>
  removeAttachmentIconColor?: ColorValue
  onPressRemove: () => void
  onPressEdit?: () => void
}) {
  const isVideo = attachment.mimeType.startsWith('video/')
  const styles = useStyles()
  const [loadingThumbnail, setLoadingThumbnail] = useState(false)
  const [thumbnailUri, setThumbnailUri] = useState<string | undefined>(
    isVideo ? undefined : attachment.uri
  )

  const { handleNavigation } = useRoutingContext()
  if (attachment.type === 'media') {
    if (isVideo) {
      if (!thumbnailUri && !loadingThumbnail) {
        setLoadingThumbnail(true)
        generateThumbnail()
          .catch(null)
          .then(result => {
            if (result.uri) {
              setThumbnailUri(result.uri)
            }
          })
          .catch(e => console.error)
          .finally(() => setLoadingThumbnail(false))
      }
    }
    return (
      <View style={[styles.mediaItemContainer, containerStyle]}>
        {isUploading ? null : (
          <Icon
            name="circle-with-cross"
            type="entypo"
            size={20}
            color={removeAttachmentIconColor ?? 'grey'}
            containerStyle={styles.removeAttachmentIconContainer}
            style={styles.removeAttachment}
            iconStyle={styles.removeAttachmentIcon}
            onPress={onPressRemove}
          />
        )}
        <PressableOpacity disabled={isUploading} onPress={onPressEdit}>
          {loadingThumbnail ? (
            <ActivityIndicator
              style={styles.attachmentPreviewImage}
              size={'small'}
            />
          ) : (
            <>
              <Image
                source={{ uri: thumbnailUri }}
                style={[styles.attachmentPreviewImage, imageStyle]}
                contentFit="cover"
              />
              {isUploading ? (
                <View style={styles.overlayIconContainer}>
                  <ActivityIndicator
                    size="small"
                    color={styles.uploadIndicator.color}
                  />
                </View>
              ) : isVideo ? (
                <View style={styles.overlayIconContainer}>
                  <Image
                    style={styles.videoPlayIcon}
                    source={require('assets/chat/play.png')}
                    contentFit="contain"
                  />
                </View>
              ) : null}
            </>
          )}
        </PressableOpacity>
      </View>
    )
  } else if (attachment.type === 'document') {
    return (
      <View style={styles.documentItemContainer}>
        {isUploading ? (
          <View style={styles.overlayIconContainer}>
            <ActivityIndicator
              size="small"
              color={styles.uploadIndicator.color}
            />
          </View>
        ) : (
          <Icon
            name="circle-with-cross"
            type="entypo"
            size={20}
            color={'grey'}
            iconStyle={styles.removeAttachmentIcon}
            containerStyle={styles.removeAttachmentIconContainer}
            style={styles.removeAttachment}
            onPress={onPressRemove}
          />
        )}
        <PressableOpacity
          onPress={() =>
            handleNavigation({
              routeName: 'pdf',
              routeParams: {
                url: attachment.uri,
                name: attachment.name,
              },
            })
          }
        >
          <View style={styles.documentAttachmentPreviewContainer}>
            <Icon
              name="file-pdf"
              type="font-awesome-5"
              solid
              size={30}
              color="grey"
            />
            <View style={styles.documentTextContainer}>
              <Text numberOfLines={2}>{attachment.name}</Text>
            </View>
          </View>
        </PressableOpacity>
      </View>
    )
  }
  return null
}

const useStyles = makeStyles((theme, darkmode) => ({
  attachmentPreviewRowContainer: {
    marginVertical: ATTACHMENT_PREVIEW_MARGIN_VERTICAL,
    marginHorizontal: ATTACHMENT_PREVIEW_MARGIN_HORIZONTAL,
    height: ATTACHMENT_PREVIEW_SIZE + ATTACHMENT_PREVIEW_MARGIN_VERTICAL,
    justifyContent: 'center',
  },
  listText: {
    fontSize: 16,
    fontWeight: 'bold',
    marginBottom: 3,
  },
  attachmentPreviewImage: {
    width: ATTACHMENT_PREVIEW_SIZE,
    height: ATTACHMENT_PREVIEW_SIZE,
    userDrag: 'none',
    backgroundColor:
      theme.mode === 'dark' ? theme.colors.grey10 : theme.colors?.white,
  },
  removeAttachmentIconContainer: {
    position: 'absolute',
    top: 0,
    right: 0,
    zIndex: 10,
  },
  removeAttachment: {
    marginTop: 7,
    marginLeft: 5,
    marginRight: 7.5,
  },
  removeAttachmentIcon: {
    backgroundColor: 'transparent',
    borderRadius: 25,
  },
  footerContainer: {
    alignItems: 'center',
    justifyContent: 'center',
    height: ATTACHMENT_PREVIEW_SIZE,
    margin: 5,
  },
  mediaItemContainer: { padding: 5 },
  documentAttachmentPreviewContainer: {
    padding: 5,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    borderWidth: 0.5,
    borderColor: 'grey',
    height: ATTACHMENT_PREVIEW_SIZE,
  },
  documentItemContainer: {
    padding: 5,
  },
  documentTextContainer: { flex: 1, paddingLeft: 5, maxWidth: 100 },
  attachmentPreviewRowContentContainer: {
    alignItems: 'center',
  },
  clearButton: { borderRadius: 20, padding: 5 },
  videoPlayIcon: { height: 25, width: 25 },
  overlayIconContainer: {
    position: 'absolute',
    justifyContent: 'center',
    alignItems: 'center',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0,0,0,0.3)',
  },
  uploadIndicator: { color: theme.colors?.primary },
}))
